/**
 * Production API Client with React Query integration
 * - Automatic token refresh
 * - Request/response interceptors
 * - Error handling
 * - Request cancellation
 */

import { API_BASE, readErrorMessage } from './api-base';
import { authService } from './auth';

export interface ApiError extends Error {
  code: string;
  status: number;
  details?: Record<string, unknown>;
}

export interface ApiResponse<T> {
  data: T;
  message?: string;
  meta?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

type RequestMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface RequestConfig {
  method?: RequestMethod;
  headers?: Record<string, string>;
  body?: unknown;
  params?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
  skipAuth?: boolean;
}

class ApiClient {
  private baseUrl: string;
  private defaultHeaders: Record<string, string>;

  constructor(baseUrl: string = '/api') {
    this.baseUrl = baseUrl;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
    };
  }

  /**
   * Build URL with query params
   */
  private buildUrl(endpoint: string, params?: Record<string, string | number | boolean | undefined>): string {
    const url = new URL(`${this.baseUrl}${endpoint}`, window.location.origin);
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          url.searchParams.append(key, String(value));
        }
      });
    }

    return url.toString();
  }

  /**
   * Handle API errors
   */
  private async handleError(response: Response): Promise<never> {
    const message = await readErrorMessage(response, 'Terjadi kesalahan');

    // Handle specific error codes
    if (response.status === 401) {
      // Token expired or invalid
      authService.clearAuth();
      window.location.href = '/login?expired=true';
    }

    // A real Error, not a plain object: every screen renders failures via
    // `error instanceof Error ? error.message : <fallback>`, so throwing an
    // object turned every backend `detail` into the generic fallback.
    const error: ApiError = Object.assign(new Error(message), {
      code: response.status === 0 ? 'NETWORK_ERROR' : 'API_ERROR',
      status: response.status,
    });
    throw error;
  }

  /**
   * Make API request
   */
  async request<T>(endpoint: string, config: RequestConfig = {}): Promise<T> {
    const {
      method = 'GET',
      headers = {},
      body,
      params,
      signal,
      skipAuth = false,
    } = config;

    // Check if token needs refresh
    if (!skipAuth && authService.isTokenExpired() && authService.getRefreshToken()) {
      try {
        await authService.refreshAccessToken();
      } catch {
        authService.clearAuth();
        window.location.href = '/login?expired=true';
        throw new Error('Session expired');
      }
    }

    const url = this.buildUrl(endpoint, params);
    const authHeaders = skipAuth ? {} : authService.getAuthHeader();

    // FormData (proof upload) must NOT get a manual Content-Type: the browser
    // writes the multipart boundary itself, and a hand-set header breaks that.
    const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

    const response = await fetch(url, {
      method,
      headers: {
        ...(isFormData ? {} : this.defaultHeaders),
        ...authHeaders,
        ...headers,
      },
      body: isFormData ? body : body ? JSON.stringify(body) : undefined,
      signal,
    });

    if (!response.ok) {
      return this.handleError(response);
    }

    // Handle empty responses
    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  }

  // Convenience methods
  get<T>(endpoint: string, params?: Record<string, string | number | boolean | undefined>, signal?: AbortSignal): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET', params, signal });
  }

  post<T>(endpoint: string, body?: unknown, config?: Omit<RequestConfig, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'POST', body });
  }

  put<T>(endpoint: string, body?: unknown, config?: Omit<RequestConfig, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'PUT', body });
  }

  patch<T>(endpoint: string, body?: unknown, config?: Omit<RequestConfig, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'PATCH', body });
  }

  delete<T>(endpoint: string, config?: Omit<RequestConfig, 'method'>): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'DELETE' });
  }
}

export const apiClient = new ApiClient(API_BASE);
export default apiClient;
